import styled from 'styled-components';
import { GetCompanyMaterialityMatrixResponse } from '@upright/common/model/output/materiality/materiality-matrix/api-types';
import { RenderViewCsrdMaterialityMatrixParams } from '@upright/common/renderer/component-types/types-render-view-csrd-matriality-matrix';
import { Text } from '@upright/ui/common/generic/components/primer-custom/text';
import { RenderViewButton } from '@upright/ui/common/generic/misc/render-view-button';
import { MaterialityMatrixArea } from './materiality-matrix-area';
import { SingleMatrixEntryWithValenceColor } from './single-matrix-matter';
import { isCompanyVariantMatrixData } from '@upright/common/model/output/materiality/materiality-matrix/types';
import { MaterialityType } from '@upright/common/model/input/materiality-assessment/types';
import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import { isOnV2DmaPath } from '@upright/ui/views/dma-v2-view/shared/utils';
import { leena2VersionMarkerSuffix } from '@upright/ui/common/upright/model/output/leena2-version-marker';
import { NavLink } from 'react-router-dom';
import { useEventTracking } from '@upright/ui/views/dma-v2-view/shared/tracking';
import { CustomGtmEvent } from '../../../../../../generic/components/google-tag-manager/use-push-gtm-event/payloads';

export const MaterialityMatrix = (props: {
  data: GetCompanyMaterialityMatrixResponse;
  entityName: string | undefined;
  imageExportParams: RenderViewCsrdMaterialityMatrixParams | undefined;
  getSingleMatterViewUrl?: (matterId: SustainabilityMatterId, materialityType: MaterialityType) => string;
  renderOptions?: { hideRenderViewButton?: boolean; showVersionInfo?: boolean };
}) => {
  const { data, entityName, renderOptions, imageExportParams, getSingleMatterViewUrl } = props;

  const isV2DmaPage = isOnV2DmaPath();
  const shouldShowVersionInfo = !!renderOptions?.showVersionInfo;
  const shouldShowRenderViewButton = !renderOptions?.hideRenderViewButton && !!entityName && !!imageExportParams;
  const shouldShowRenderViewButtonInBottom = shouldShowRenderViewButton && !isV2DmaPage;
  const hasBottomAreaContent = shouldShowVersionInfo || shouldShowRenderViewButtonInBottom;

  const trackEvent = useEventTracking();

  const renderViewButton = () => {
    // For the DMA Freemium page we show a custom not available message about image download licensing
    // and direct user to the plans page
    const notAvailableCustomContent = isV2DmaPage
      ? {
          title: 'Access restricted',
          ingress: (
            <>
              <div>This feature isn’t included in your current plan.</div>
              <br />
              <div>
                <NavLink to="/dashboard/settings/plans">View plans</NavLink> to upgrade and unlock access.
              </div>
            </>
          ),
        }
      : undefined;

    return shouldShowRenderViewButton ? (
      <RenderViewButton
        trackEvent={() => {
          trackEvent({ event: CustomGtmEvent.PLG_MATERIALITY_MATRIX_IMAGE_DOWNLOADED });
        }}
        initialOptions={{
          width: 1400,
          componentParams: [imageExportParams],
          showControlUi: true,
        }}
        notAvailableCustomContent={notAvailableCustomContent}
      />
    ) : null;
  };

  return (
    <Container>
      <HeaderContainer $isV2DmaPage={isV2DmaPage}>
        <HeaderTopRow $isV2DmaPage={isV2DmaPage}>
          <Title>Financial vs impact materiality</Title>
        </HeaderTopRow>
        <HeaderContent>
          <Text color="fg.muted">
            {entityName ? `${entityName} has` : 'There are'} <strong>{data.materialMatterCount}</strong> material
            sustainability matters.
          </Text>
          <MaterialityMatrixLegend />
        </HeaderContent>
      </HeaderContainer>
      <MatrixContainer>
        <MaterialityMatrixArea matters={data.matrixData} getSingleMatterViewUrl={getSingleMatterViewUrl} />
      </MatrixContainer>
      {hasBottomAreaContent && (
        <BottomContainer>
          <div>
            {shouldShowVersionInfo && (
              <VersionInfo>
                Release {data.entityData.release}
                {isCompanyVariantMatrixData(data.entityData) && (
                  <>
                    <br />
                    Version {data.entityData.productDataVersion}
                    {leena2VersionMarkerSuffix(data.entityData.productDataVersion)}
                  </>
                )}
              </VersionInfo>
            )}
          </div>
          {!isV2DmaPage && <RenderButtonContainer>{renderViewButton()}</RenderButtonContainer>}
        </BottomContainer>
      )}
    </Container>
  );
};

const MaterialityMatrixLegend = () => {
  return (
    <LegendContainer>
      <SingleMatrixEntryWithValenceColor valence="pos">Positive materiality</SingleMatrixEntryWithValenceColor>
      <SingleMatrixEntryWithValenceColor valence="neg">Negative materiality</SingleMatrixEntryWithValenceColor>
      <SingleMatrixEntryWithValenceColor valence="posneg">
        Positive and negative materiality
      </SingleMatrixEntryWithValenceColor>
    </LegendContainer>
  );
};

const Container = styled.div``;

const HeaderContainer = styled.div<{ $isV2DmaPage?: boolean }>`
  padding-top: ${p => (p.$isV2DmaPage ? '0' : '12px')};
  padding-left: 44px;
  padding-right: 20px;
`;

const HeaderTopRow = styled.div<{ $isV2DmaPage?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const Title = styled.p`
  font-weight: bold;
  font-size: 16px;
  margin: 0;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 1200px) {
    flex-direction: row;
  }
`;

const MatrixContainer = styled.div`
  padding: 12px 20px;
`;

const BottomContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const VersionInfo = styled.div`
  font-size: 12px;
  padding-left: 44px;
  padding-right: 12px;
  padding-bottom: 20px;
  color: #6a737d;
`;

const RenderButtonContainer = styled.div`
  padding-left: 12px;
  padding-right: 20px;
  padding-bottom: 12px;
`;

const LegendContainer = styled.div`
  display: flex;
  gap: 8px;
  white-space: nowrap;
`;
